// Fetch the as-built topology from Supabase for the map.
// Positions are stored as PostGIS geometry(Point,4326); Supabase serialises
// them as inline GeoJSON ({type:"Point",coordinates:[lng,lat]}), which we
// flatten to lng/lat here so view components stay geometry-agnostic.
import { supabase } from "./supabase";
import { getProjectId } from "./persistence";
import type { Field } from "@/types";

export interface TopoTable {
  field_no: number;
  table_no: number;
  type: string;
  /** Block rotation (degrees CCW) in the ITM grid; used to orient the rectangle. */
  rot: number;
  /** Reprojected block INSERTION point (the table's NW corner, not its centroid). */
  lng: number;
  lat: number;
  /** Real CAD row number (PVcase Row Numbering layer, migration 0024). Undefined
   *  when the topology predates the column / wasn't ingested. */
  row_no?: number;
}
export interface TopoInverter {
  field_no: number;
  idx: number;
  lng: number;
  lat: number;
  /** Real CAD inverter designator e.g. "TR7-INV1" (PVcase Device Numbering layer,
   *  migration 0025). Undefined when the column isn't present. */
  label?: string;
}

interface GeoPoint {
  type: "Point";
  coordinates: [number, number];
}
const coords = (g: GeoPoint | null): [number, number] => (g?.coordinates ?? [NaN, NaN]);

// ---- Offline topology cache (IndexedDB) ------------------------------------
// The map's overlay DATA (tables/piles/inverters/…) comes from Supabase, so a
// cold offline reload would otherwise fail ("Failed to load topology"). We mirror
// each assembled result into IndexedDB keyed by resource+project, and fall back
// to it when the network is unavailable. Network-first: a successful (non-empty)
// fetch refreshes the cache; an error or an empty offline result reads it back.
const TOPO_DB = "gridproof-topo-cache";
const TOPO_STORE = "topo";
function topoIdb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") return resolve(null);
      const req = indexedDB.open(TOPO_DB, 1);
      req.onupgradeneeded = () => { if (!req.result.objectStoreNames.contains(TOPO_STORE)) req.result.createObjectStore(TOPO_STORE); };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch { resolve(null); }
  });
}
async function topoCacheGet<T>(key: string): Promise<T | null> {
  const db = await topoIdb(); if (!db) return null;
  return new Promise((resolve) => {
    try {
      const r = db.transaction(TOPO_STORE, "readonly").objectStore(TOPO_STORE).get(key);
      r.onsuccess = () => resolve((r.result as T) ?? null);
      r.onerror = () => resolve(null);
    } catch { resolve(null); }
  });
}
async function topoCacheSet<T>(key: string, val: T): Promise<void> {
  const db = await topoIdb(); if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(TOPO_STORE, "readwrite");
      tx.objectStore(TOPO_STORE).put(val as any, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch { resolve(); }
  });
}
/** Network-first wrapper: cache non-empty live results; fall back to the cached
 *  copy on error or when an offline build returns empty. */
async function withTopoCache<T>(resource: string, fetcher: () => Promise<T>): Promise<T> {
  const key = `${resource}:${getProjectId()}`;
  try {
    const data = await fetcher();
    const nonEmpty = Array.isArray(data) ? data.length > 0 : data != null;
    if (nonEmpty) { topoCacheSet(key, data).catch(() => {}); return data; }
    const cached = await topoCacheGet<T>(key);
    return cached != null ? cached : data;
  } catch (err) {
    const cached = await topoCacheGet<T>(key);
    if (cached != null) return cached;
    throw err;
  }
}

export function fetchTopoTables(): Promise<TopoTable[]> { return withTopoCache("tables", fetchTopoTablesLive); }
async function fetchTopoTablesLive(): Promise<TopoTable[]> {
  if (!supabase) return [];
  // PostgREST caps responses at ~1,000 rows; page through with range().
  const PAGE = 1000;
  const out: TopoTable[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from("topo_tables")
      .select("field_no,table_no,type,rot,row_no,geom")
      .eq("project_id", getProjectId())
      .not("geom", "is", null)
      .order("field_no")
      .order("table_no")
      .range(from, from + PAGE - 1);
    if (error) throw error;
    const rows = (data || []) as any[];
    for (const r of rows) {
      const [lng, lat] = coords(r.geom);
      out.push({ field_no: r.field_no, table_no: r.table_no, type: r.type, rot: Number(r.rot) || 0, lng, lat, row_no: r.row_no ?? undefined });
    }
    if (rows.length < PAGE) break;
  }
  return out;
}

export interface TopoStation {
  id: string;
  name: string;
  rating_mva: number;
  lng: number;
  lat: number;
}
export function fetchTopoStations(): Promise<TopoStation[]> { return withTopoCache("stations", fetchTopoStationsLive); }
async function fetchTopoStationsLive(): Promise<TopoStation[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.from("topo_stations").select("id,name,rating_mva,geom").eq("project_id", getProjectId()).not("geom", "is", null);
  if (error) throw error;
  return ((data || []) as any[]).map((r) => {
    const [lng, lat] = coords(r.geom);
    return { id: r.id, name: r.name, rating_mva: r.rating_mva, lng, lat };
  });
}

export function fetchTopoInverters(): Promise<TopoInverter[]> { return withTopoCache("inverters", fetchTopoInvertersLive); }
async function fetchTopoInvertersLive(): Promise<TopoInverter[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from("topo_inverters")
    .select("field_no,idx,label,geom")
    .eq("project_id", getProjectId())
    .not("geom", "is", null)
    .limit(2000);
  if (error) throw error;
  return ((data || []) as any[]).map((r) => {
    const [lng, lat] = coords(r.geom);
    return { field_no: r.field_no, idx: r.idx, lng, lat, label: r.label ?? undefined };
  });
}

export interface TopoPile {
  field_no: number;
  table_no: number;
  pile_idx: number;
  depth_m: number | null;
  method: string | null;
  color_class: string | null;
  length_m: number | null;
  lng: number;
  lat: number;
}

/** Foundation piles, keyed (field_no, table_no, pile_idx) — see migration 0010. */
export function fetchTopoPiles(): Promise<TopoPile[]> { return withTopoCache("piles", fetchTopoPilesLive); }
async function fetchTopoPilesLive(): Promise<TopoPile[]> {
  if (!supabase) return [];
  const PAGE = 1000;
  const out: TopoPile[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from("topo_piles")
      .select("field_no,table_no,pile_idx,depth_m,method,color_class,length_m,geom")
      .eq("project_id", getProjectId())
      .not("geom", "is", null)
      .order("field_no")
      .order("table_no")
      .order("pile_idx")
      .range(from, from + PAGE - 1);
    if (error) throw error;
    const rows = (data || []) as any[];
    for (const r of rows) {
      const [lng, lat] = coords(r.geom);
      out.push({
        field_no: r.field_no, table_no: r.table_no, pile_idx: r.pile_idx,
        depth_m: r.depth_m, method: r.method, color_class: r.color_class,
        length_m: r.length_m, lng, lat,
      });
    }
    if (rows.length < PAGE) break;
  }
  return out;
}

export interface TopoFieldZone {
  field_no: number;
  /** Outer ring as [lng,lat] pairs (closed). */
  ring: [number, number][];
}

interface GeoPolygon { type: "Polygon"; coordinates: [number, number][][]; }

/** Real per-field zone polygons (migration 0012, from the ACAD-ZIM PVcase PV
 *  Area layer). Empty array if the `zone` column hasn't been populated yet —
 *  callers fall back to the client-side convex hull. */
export function fetchTopoFieldZones(): Promise<TopoFieldZone[]> { return withTopoCache("zones", fetchTopoFieldZonesLive); }
async function fetchTopoFieldZonesLive(): Promise<TopoFieldZone[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.from("topo_fields").select("field_no,zone").eq("project_id", getProjectId()).not("zone", "is", null);
  if (error) {
    // `zone` column may not exist before the migration is applied — degrade gracefully.
    if ((error as any).code === "42703" || /zone/.test(error.message || "")) return [];
    throw error;
  }
  const out: TopoFieldZone[] = [];
  for (const r of (data || []) as any[]) {
    const g = r.zone as GeoPolygon | null;
    const ring = g?.coordinates?.[0];
    if (ring && ring.length >= 4) out.push({ field_no: r.field_no, ring: ring as [number, number][] });
  }
  return out;
}

export interface TopoRoad {
  /** Line ring as [lng,lat] pairs. */
  path: [number, number][];
  kind: string | null;
}

interface GeoLineString { type: "LineString"; coordinates: [number, number][]; }
interface GeoMultiLineString { type: "MultiLineString"; coordinates: [number, number][][]; }

/** Internal access roads (issue #9). The road/fence geometry was not present in
 *  the DXFs ingested so far (the road layers came in empty and `topo_lines` was
 *  dropped), so this returns [] today and the map draws nothing — the layer
 *  plumbing is wired so roads appear automatically once a `topo_lines` table
 *  with `kind='road'` is ingested for the project. Degrades gracefully if the
 *  table/column doesn't exist yet. */
export function fetchTopoRoads(): Promise<TopoRoad[]> { return withTopoCache("roads", fetchTopoRoadsLive); }
async function fetchTopoRoadsLive(): Promise<TopoRoad[]> {
  if (!supabase) return [];
  try {
    const { data, error } = await supabase
      .from("topo_lines")
      .select("kind,geom")
      .eq("project_id", getProjectId())
      .eq("kind", "road")
      .not("geom", "is", null);
    // Roads are optional and the source table was reverted (migration 0008), so
    // any error here (missing table/column) just means "no roads yet" — never
    // let it fail the map load.
    if (error) return [];
    const out: TopoRoad[] = [];
    for (const r of (data || []) as any[]) {
      const g = r.geom as GeoLineString | GeoMultiLineString | null;
      if (!g) continue;
      if (g.type === "LineString" && g.coordinates?.length >= 2) out.push({ kind: r.kind, path: g.coordinates as [number, number][] });
      else if (g.type === "MultiLineString") for (const seg of g.coordinates) if (seg?.length >= 2) out.push({ kind: r.kind, path: seg as [number, number][] });
    }
    return out;
  } catch {
    return [];
  }
}

// ---- Topology 1:1 integrity check (issue #11) ------------------------------
// Confirms the engine's per-table layout (what QC inspects) matches the map
// topology (topo_tables / topo_piles) 1:1 for the active project, so a project
// can never present a map that disagrees with its inspection navigator.

export interface TopologyIntegrityReport {
  ok: boolean;
  project: string;
  engineTables: number;
  topoTables: number;
  engineModules: number;
  enginePiles: number;
  topoPiles: number;
  /** Up to a few human-readable mismatch descriptions (empty when ok). */
  issues: string[];
}

/** Exact row count for a project-scoped topo table via a HEAD request (no rows
 *  transferred). Returns -1 if the table/column is missing so callers can skip. */
async function projectRowCount(table: string): Promise<number> {
  if (!supabase) return -1;
  const { count, error } = await supabase.from(table).select("*", { count: "exact", head: true }).eq("project_id", getProjectId());
  return error ? -1 : count ?? 0;
}

/** Compare the engine's installed topology (D.fields + per-table types, already
 *  in memory) against the source topo_* row counts. Cheap — two HEAD/count
 *  queries, no row payloads — so it's safe to run on every boot. Logs a warning
 *  on mismatch (the key signal: the engine fell back to the demo seed while the
 *  project actually has CAD topology). No-ops gracefully without Supabase. */
export async function verifyTopologyIntegrity(): Promise<TopologyIntegrityReport | null> {
  const e = (window as any).__gpEngine;
  if (!e || !supabase) return null;
  const [topoTables, topoPiles] = await Promise.all([projectRowCount("topo_tables"), projectRowCount("topo_piles")]);
  if (topoTables <= 0) return null; // project has no CAD topology (or query failed) — nothing to verify

  const modOf = (type: string) => MODULES_PER_TABLE[type] ?? 0;
  let engineTables = 0, engineModules = 0, enginePiles = 0;
  for (const f of e.D?.fields || []) {
    engineTables += f.tables || 0;
    for (let t = 1; t <= f.tables; t++) {
      engineModules += modOf(e.tableTypeFor(f, t));
      const tt = e.D.tableTypes[e.tableTypeFor(f, t)];
      enginePiles += (tt?.pilesPerRow ?? 0) * 2;
    }
  }

  const issues: string[] = [];
  if (engineTables !== topoTables) issues.push(`Table count: engine ${engineTables} vs topology ${topoTables}`);
  // Piles: the real ZIM survey (0013) carries ~19k piles vs the engine's idealised
  // per-table rows, so an exact match isn't expected — only flag a genuine 1:1
  // break (topology has none while the engine expects some).
  if (topoPiles === 0 && enginePiles > 0) issues.push(`Piles: topology has none but engine expects ${enginePiles}`);

  const report: TopologyIntegrityReport = {
    ok: issues.length === 0,
    project: getProjectId(),
    engineTables, topoTables, engineModules, enginePiles, topoPiles, issues,
  };
  if (!report.ok) console.warn("[topology] 1:1 integrity check found mismatches:", report.issues, report);
  else console.info("[topology] 1:1 integrity check OK", { tables: topoTables, modules: engineModules, piles: topoPiles });
  return report;
}

// ---- Canonical topology → engine D.fields payload --------------------------
// The engine seeds a synthetic demo (D.fields, pseudo-random table types). At
// boot we replace that with the real CAD layout so QC inspects the exact tables
// the CAD defines (and the map can colour them by inspection status). This is
// the single element identity ("Field N::t") that QC keys against. See
// engine.applyTopology + CLAUDE.md.

const MODULES_PER_TABLE: Record<string, number> = { "3x27": 81, "3x18": 54, "3x9": 27 };
// Display-only DC capacity estimate: 2382×1134 mm large-format module ≈ 0.66 kWp.
const MODULE_WP = 660;

export interface TopologyPayload {
  fields: Field[];
  typeByKey: Record<string, string>;
}

/** Assemble the engine topology payload from topo_tables. Returns null if empty. */
export async function fetchCanonicalTopology(): Promise<TopologyPayload | null> {
  const tables = await fetchTopoTables();
  if (!tables.length) return null;

  // Site bounding box → schematic (0..100) normalisation; north (max lat) on top.
  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
  for (const t of tables) {
    if (!isFinite(t.lng) || !isFinite(t.lat)) continue;
    if (t.lng < minLng) minLng = t.lng;
    if (t.lng > maxLng) maxLng = t.lng;
    if (t.lat < minLat) minLat = t.lat;
    if (t.lat > maxLat) maxLat = t.lat;
  }
  const spanLng = maxLng - minLng || 1;
  const spanLat = maxLat - minLat || 1;

  const byField = new Map<number, TopoTable[]>();
  const typeByKey: Record<string, string> = {};
  for (const t of tables) {
    let arr = byField.get(t.field_no);
    if (!arr) byField.set(t.field_no, (arr = []));
    arr.push(t);
    typeByKey[`Field ${t.field_no}::${t.table_no}`] = t.type;
  }

  const fields: Field[] = [];
  for (const fno of [...byField.keys()].sort((a, b) => a - b)) {
    const fts = byField.get(fno)!;
    let fMinLng = Infinity, fMaxLng = -Infinity, fMinLat = Infinity, fMaxLat = -Infinity, modules = 0;
    for (const t of fts) {
      modules += MODULES_PER_TABLE[t.type] ?? 0;
      if (!isFinite(t.lng) || !isFinite(t.lat)) continue;
      if (t.lng < fMinLng) fMinLng = t.lng;
      if (t.lng > fMaxLng) fMaxLng = t.lng;
      if (t.lat < fMinLat) fMinLat = t.lat;
      if (t.lat > fMaxLat) fMaxLat = t.lat;
    }
    fields.push({
      id: `Field ${fno}`,
      tables: fts.length,
      mwp: Math.round((modules * MODULE_WP) / 1e6 * 10) / 10,
      done: 0, // progress is derived from real inspection records, not seeded
      ncr: 0,
      x: Math.round(((fMinLng - minLng) / spanLng) * 100),
      y: Math.round(((maxLat - fMaxLat) / spanLat) * 100), // invert: north on top
      w: Math.max(2, Math.round(((fMaxLng - fMinLng) / spanLng) * 100)),
      h: Math.max(2, Math.round(((fMaxLat - fMinLat) / spanLat) * 100)),
    });
  }
  return { fields, typeByKey };
}

// ---- Geometry helpers: table rectangles + field/site hulls -----------------
// The map renders tables as oriented rectangles (not dots). Each table's stored
// `geom` is the block INSERTION point (NW corner), and the block footprint runs
// +X (east) and -Y (south) in the ITM grid, rotated by `rot` (degrees CCW).
// We build the 4 corners in metres about the anchor and convert to lng/lat with
// a local equirectangular offset — accurate to well under a metre over a ~30 m
// table at this latitude, so no proj4/round-trip to PostGIS is needed.

/** Table footprint width (m, along the panel run) by type. Depth is constant. */
export const TABLE_WIDTH_M: Record<string, number> = { "3x27": 31.164, "3x18": 20.769, "3x9": 10.374 };
export const TABLE_DEPTH_M = 6.753;
/** Module columns (along the width) and pile count per row (per long edge) by type. */
export const TABLE_COLS: Record<string, number> = { "3x27": 27, "3x18": 18, "3x9": 9 };
export const TABLE_ROWS = 3;
export const TABLE_PILES_PER_ROW: Record<string, number> = { "3x27": 14, "3x18": 10, "3x9": 5 };
const M_PER_DEG_LAT = 110540;
const M_PER_DEG_LNG = 111320; // scaled by cos(lat) per-point

// Issue #13 (client request): some fields' CAD block rotations still rendered
// the tables vertical/portrait on the map even though the panel rows physically
// run east-west. Rather than maintain a per-field allow-list (originally fields
// {3,7}), the client asked to force EVERY table to landscape (rot=0). This is a
// deliberate global visual override, not a geometry derivation — flip
// FORCE_ALL_HORIZONTAL to false to fall back to the per-field allow-list.
const FORCE_ALL_HORIZONTAL = true;
const FORCE_HORIZONTAL_FIELDS = new Set<number>([3, 7]);
/** Effective block rotation: 0 (axis-aligned landscape) when the global
 *  override is on (or for the per-field overrides), otherwise the CAD rotation. */
function effRot(t: TopoTable): number {
  if (FORCE_ALL_HORIZONTAL) return 0;
  return FORCE_HORIZONTAL_FIELDS.has(t.field_no) ? 0 : t.rot;
}

/** Map a table-local offset (metres east of insert, metres north) to [lng,lat],
 *  honouring the block rotation. Shared by the rectangle / module / pile helpers. */
function localToLngLat(t: TopoTable, lx: number, ly: number): [number, number] {
  const th = (effRot(t) * Math.PI) / 180;
  const cos = Math.cos(th), sin = Math.sin(th);
  const cosLat = Math.cos((t.lat * Math.PI) / 180) || 1;
  const ex = lx * cos - ly * sin; // metres east
  const ny = lx * sin + ly * cos; // metres north
  return [t.lng + ex / (M_PER_DEG_LNG * cosLat), t.lat + ny / M_PER_DEG_LAT];
}

/** Closed [lng,lat] ring (5 points) for a table's footprint rectangle. */
export function tablePolygon(t: TopoTable): [number, number][] {
  const w = TABLE_WIDTH_M[t.type] ?? TABLE_WIDTH_M["3x27"];
  const d = TABLE_DEPTH_M;
  // local corners (lx east-ish, ly north-ish): NW(0,0) NE(w,0) SE(w,-d) SW(0,-d)
  const ring: [number, number][] = [[0, 0], [w, 0], [w, -d], [0, -d]].map(([lx, ly]) => localToLngLat(t, lx, ly));
  ring.push(ring[0]);
  return ring;
}

/** Closed [lng,lat] ring for one module cell of a table (3 rows × cols grid).
 *  Module index m matches the engine's scheme: row = floor(m/cols), col = m%cols. */
export function modulePolygon(t: TopoTable, m: number): [number, number][] {
  const w = TABLE_WIDTH_M[t.type] ?? TABLE_WIDTH_M["3x27"];
  const cols = TABLE_COLS[t.type] ?? TABLE_COLS["3x27"];
  const d = TABLE_DEPTH_M;
  const row = Math.floor(m / cols), col = m % cols;
  const x0 = (col / cols) * w, x1 = ((col + 1) / cols) * w;
  const y0 = -(row / TABLE_ROWS) * d, y1 = -((row + 1) / TABLE_ROWS) * d;
  const ring: [number, number][] = [[x0, y0], [x1, y0], [x1, y1], [x0, y1]].map(([lx, ly]) => localToLngLat(t, lx, ly));
  ring.push(ring[0]);
  return ring;
}

/** Per-table foundation piles derived from the table footprint: two rows (N/S)
 *  of `pilesPerRow` piles. Index matches the engine's pile scheme used by
 *  pileRoll/pkey — 0..pilesPerRow-1 = north row, pilesPerRow..2n-1 = south row. */
export function tablePiles(t: TopoTable): { idx: number; lng: number; lat: number }[] {
  const w = TABLE_WIDTH_M[t.type] ?? TABLE_WIDTH_M["3x27"];
  const n = TABLE_PILES_PER_ROW[t.type] ?? TABLE_PILES_PER_ROW["3x27"];
  const d = TABLE_DEPTH_M;
  const yNorth = -0.18 * d, ySouth = -0.82 * d; // inboard from the long edges
  const out: { idx: number; lng: number; lat: number }[] = [];
  for (let k = 0; k < n; k++) {
    const x = ((k + 0.5) / n) * w;
    const [nlng, nlat] = localToLngLat(t, x, yNorth);
    out.push({ idx: k, lng: nlng, lat: nlat });
    const [slng, slat] = localToLngLat(t, x, ySouth);
    out.push({ idx: n + k, lng: slng, lat: slat });
  }
  return out;
}

/** Andrew's monotone-chain convex hull over [lng,lat] points. */
export function convexHull(pts: [number, number][]): [number, number][] {
  const p = pts.filter(([x, y]) => isFinite(x) && isFinite(y)).slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (p.length < 3) return p;
  const cross = (o: number[], a: number[], b: number[]) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: [number, number][] = [];
  for (const q of p) { while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], q) <= 0) lower.pop(); lower.push(q); }
  const upper: [number, number][] = [];
  for (let i = p.length - 1; i >= 0; i--) { const q = p[i]; while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], q) <= 0) upper.pop(); upper.push(q); }
  lower.pop(); upper.pop();
  return lower.concat(upper);
}

/** Approximate-metre offset polygon-padding: expand a hull outward by `m` metres. */
export function padHull(hull: [number, number][], m: number): [number, number][] {
  if (hull.length < 3) return hull;
  let cx = 0, cy = 0;
  for (const [x, y] of hull) { cx += x; cy += y; }
  cx /= hull.length; cy /= hull.length;
  const cosLat = Math.cos((cy * Math.PI) / 180) || 1;
  return hull.map(([x, y]) => {
    const dx = (x - cx) * M_PER_DEG_LNG * cosLat;
    const dy = (y - cy) * M_PER_DEG_LAT;
    const len = Math.hypot(dx, dy) || 1;
    return [x + (dx / len) * m / (M_PER_DEG_LNG * cosLat), y + (dy / len) * m / M_PER_DEG_LAT] as [number, number];
  });
}

/** Circle polygon (for the GPS accuracy ring), `r` metres around [lng,lat]. */
export function circlePolygon(lng: number, lat: number, r: number, steps = 48): [number, number][] {
  const cosLat = Math.cos((lat * Math.PI) / 180) || 1;
  const ring: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * 2 * Math.PI;
    ring.push([lng + (Math.cos(a) * r) / (M_PER_DEG_LNG * cosLat), lat + (Math.sin(a) * r) / M_PER_DEG_LAT]);
  }
  return ring;
}

/** Load the canonical topology and install it into the headless engine. */
export async function applyCanonicalTopology(): Promise<boolean> {
  try {
    const payload = await fetchCanonicalTopology();
    if (!payload) return false;
    const e = (window as any).__gpEngine;
    return e?.applyTopology?.(payload) ?? false;
  } catch (err) {
    console.warn("[topology] canonical load failed; keeping demo seed", err);
    return false;
  }
}

// Build the QC Electrical station/inverter model from the REAL CAD topology so the
// inverter numbering + per-station counts match the Site Map exactly (client issue:
// "inverter numbers have to be same as on the map / each station has a certain number
// of inverters"). Groups topo_inverters by the "TR{n}" prefix of their real
// designator (topo_inverters.label = "TR{n}-INV{m}"), sorts each station's inverters
// by INV number, and resolves each station's field from its inverters' field_no.
export async function applyInverterTopology(): Promise<boolean> {
  try {
    const e = (window as any).__gpEngine;
    if (!e?.applyInverterTopology) return false;
    const invs = await fetchTopoInverters();
    if (!invs.length) return false;

    const groups = new Map<number, { inverters: { label: string; m: number }[]; fields: number[] }>();
    let labeled = 0;
    for (const inv of invs) {
      const mt = inv.label?.match(/^TR(\d+)-INV(\d+)$/i);
      if (!mt) continue;
      labeled++;
      const tr = +mt[1];
      let g = groups.get(tr);
      if (!g) { g = { inverters: [], fields: [] }; groups.set(tr, g); }
      g.inverters.push({ label: inv.label!, m: +mt[2] });
      if (inv.field_no != null) g.fields.push(inv.field_no);
    }
    // No usable CAD designators (offline / topology not ingested) → keep the synthetic model.
    if (!groups.size || labeled < invs.length * 0.5) return false;

    const old: any[] = (e.D && e.D.stations) || [];
    const mode = (xs: number[]) => {
      const c = new Map<number, number>();
      let best = xs[0], bestN = 0;
      for (const x of xs) { const n = (c.get(x) || 0) + 1; c.set(x, n); if (n > bestN) { bestN = n; best = x; } }
      return best;
    };
    const stations = [...groups.keys()].sort((a, b) => a - b).map((tr) => {
      const g = groups.get(tr)!;
      const inverters = g.inverters.sort((a, b) => a.m - b.m).map((x) => ({ label: x.label }));
      const meta = old[tr - 1] || {};
      const fld = g.fields.length ? mode(g.fields) : null;
      return {
        id: `TR${tr}`,
        label: `Transformer TR${tr}`,
        area: fld != null ? `Field ${fld}` : (meta.area || ""),
        padHeight: meta.padHeight,
        ref: `TRANSFORMER T${String(tr).padStart(2, "0")}`,
        invCount: inverters.length,
        inverters,
      };
    });
    return e.applyInverterTopology({ stations }) ?? false;
  } catch (err) {
    console.warn("[topology] inverter model load failed; keeping synthetic stations", err);
    return false;
  }
}
